/**
 * useCandidateFiltering Hook
 *
 * Manages filter state, debounced search, and loads filter options for candidate lists.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import {
  CandidateFilterOptions,
  CandidateFilterState,
  CandidateFiltersConfig,
  SortState,
  DEFAULT_FILTER_STATE,
  DEFAULT_SORT_STATE,
  DEFAULT_FILTERS_CONFIG,
  CandidateSortField,
  SortDirection,
} from './types';

interface UseCandidateFilteringOptions {
  config?: CandidateFiltersConfig;
  defaultFilters?: Partial<CandidateFilterState>;
}

interface UseCandidateFilteringReturn {
  filters: CandidateFilterState;
  sort: SortState;
  filterOptions: CandidateFilterOptions | undefined;
  isLoadingOptions: boolean;
  config: CandidateFiltersConfig;
  setSearchQuery: (query: string) => void;
  setFilter: (key: keyof CandidateFilterState, value: string) => void;
  setSort: (field: CandidateSortField) => void;
  setSortDirect: (state: SortState) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

export function useCandidateFiltering(
  options: UseCandidateFilteringOptions = {}
): UseCandidateFilteringReturn {
  const { config = DEFAULT_FILTERS_CONFIG, defaultFilters } = options;

  const initialFilters = useMemo<CandidateFilterState>(() => ({
    ...DEFAULT_FILTER_STATE,
    ...defaultFilters,
  }), [defaultFilters]);

  const [filters, setFilters] = useState<CandidateFilterState>(initialFilters);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const { data: filterOptions, isLoading: isLoadingOptions } = useQuery<CandidateFilterOptions>(
    ['candidate-filter-options'],
    async () => {
      const response = await api.get('/candidates/filter-options');
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error('Failed to load filter options');
    },
    { staleTime: 5 * 60 * 1000 }
  );

  /*
    Status defaults to Active, but the vocabulary lives in the role_statuses table
    rather than in code, so the exact stored casing is not knowable at build time.
    DEFAULT_FILTER_STATE carries the intent ('active'); once the options arrive,
    reconcile it against what the server actually returned — matching
    case-insensitively, and falling back to no filter if the deployment has no
    Active status at all. Without this a casing mismatch would leave the select
    showing a blank value while quietly filtering on a status nothing has.
  */
  const statusDefault = useMemo(() => {
    const statuses = filterOptions?.statuses;
    if (!statuses?.length) return initialFilters.status;
    const wanted = initialFilters.status.toLowerCase();
    return statuses.find(s => s.toLowerCase() === wanted) ?? 'All';
  }, [filterOptions, initialFilters.status]);

  useEffect(() => {
    // Only adopt the reconciled value while the filter is still untouched, so a
    // user who has already picked a status keeps it when options resolve.
    setFilters(prev =>
      prev.status === initialFilters.status && prev.status !== statusDefault
        ? { ...prev, status: statusDefault }
        : prev
    );
  }, [statusDefault, initialFilters.status]);

  const setSearchQuery = useCallback((query: string) => {
    setFilters(prev => ({ ...prev, searchQuery: query }));
  }, []);

  const setFilter = useCallback((key: keyof CandidateFilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSort = useCallback((field: CandidateSortField) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc' as SortDirection,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    // Back to the reconciled Active value, not the raw intent, so Clear does not
    // reintroduce a status the server never returned.
    setFilters({ ...initialFilters, status: statusDefault });
  }, [initialFilters, statusDefault]);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.searchQuery !== '' ||
      (filters.officeSought !== 'All' && filters.officeSought !== DEFAULT_FILTER_STATE.officeSought) ||
      (filters.party !== 'All parties' && filters.party !== DEFAULT_FILTER_STATE.party) ||
      (filters.district !== 'All districts' && filters.district !== DEFAULT_FILTER_STATE.district) ||
      (filters.electionYear !== DEFAULT_FILTER_STATE.electionYear) ||
      // Status now defaults to Active, so "not All" no longer means "user changed
      // it" — comparing against the default (as the other rows do) is what keeps
      // Clear Filters from lighting up on a freshly loaded page.
      (filters.status !== DEFAULT_FILTER_STATE.status && filters.status !== statusDefault)
    );
  }, [filters, statusDefault]);

  return {
    filters,
    sort,
    filterOptions,
    isLoadingOptions,
    config,
    setSearchQuery,
    setFilter,
    setSort: handleSort,
    setSortDirect: setSort,
    clearFilters,
    hasActiveFilters,
  };
}

export { applySortToData } from './candidateSortUtils';
